import { Fragment, type MouseEvent, type ReactNode } from 'react';
import type { InvoicedCustomer } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { MORE_CHIP_BASE_CLASS, MORE_CHIP_SIZE_CLASS } from './moreChip';

export type InvoicedCustomersVariant = 'stack' | 'compact' | 'list';

/** Variants that collapse all but the first name into a `+N` chip. */
type CollapsingVariant = Exclude<InvoicedCustomersVariant, 'list'>;

interface InvoicedCustomersProps {
  /** Customers whose invoices the payment covers, in display order. */
  customers: readonly InvoicedCustomer[];
  /**
   * `stack` (detail panels) shows one name, emphasized; `compact` (table cells,
   * rail cards) shows one. Either way the rest collapse into a `+N` button
   * that opens the full list. `list` shows every name, one per line, with
   * no `+N`.
   */
  variant?: InvoicedCustomersVariant;
  /**
   * Size of the `+N` chip. Defaults to the variant's own size; pass
   * `compact` to get the small chip in a detail panel.
   */
  chipSize?: CollapsingVariant;
  /** Rendered instead when there are no invoiced customers. */
  fallback?: ReactNode;
  className?: string;
}

/**
 * Clicks inside the list must not reach a clickable ancestor (a table row
 * that opens a slide-in). React propagates through portals, so the popover
 * content needs this too.
 */
function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}

/**
 * Detail panels give the invoiced customers the most weight on the card —
 * whose invoices were paid matters more than who sent the money. Table cells
 * stay regular weight so they don't shout in a dense list.
 */
function NameLink({
  customer,
  emphasized = false,
}: {
  customer: InvoicedCustomer;
  emphasized?: boolean;
}) {
  return (
    <CustomerLink
      customerId={customer.id}
      customerName={customer.name}
      erpDisplayName={customer.erpDisplayName}
      onClick={stopPropagation}
      className={emphasized ? 'font-medium' : 'font-normal'}
    />
  );
}

function MoreCustomersPopover({
  customers,
  hiddenCount,
  chipSize,
}: {
  customers: readonly InvoicedCustomer[];
  hiddenCount: number;
  chipSize: CollapsingVariant;
}) {
  const gt = useGT();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            MORE_CHIP_BASE_CLASS,
            'cursor-pointer hover:bg-secondary/70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
            MORE_CHIP_SIZE_CLASS[chipSize],
          )}
          onClick={stopPropagation}
          aria-label={gt('Show all {count} invoiced customers', {
            context:
              'a11y label, opens the full list of customers whose invoices a payment covers',
            count: customers.length,
          })}
        >
          +{hiddenCount}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto max-w-sm p-3"
        align="start"
        onClick={stopPropagation}
      >
        <div className="mb-2 text-xs font-medium text-muted-foreground">
          <T context="popover heading, customers whose invoices a payment covers">
            Invoiced customers
          </T>
        </div>
        <ul
          className="space-y-1"
          aria-label={gt('Invoiced customers', {
            context:
              'a11y label, list of customers whose invoices a payment covers',
          })}
        >
          {customers.map((customer) => (
            <li key={customer.id}>
              <NameLink customer={customer} />
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

/**
 * The customers whose invoices a payment covers — distinct from the payer who
 * sent the money. One check can settle invoices billed to several customers
 * (sister companies, a parent paying for its branches). Rendered as a plain
 * comma-separated list of links so it reads at the same weight as the payer.
 */
export function InvoicedCustomers({
  customers,
  variant = 'stack',
  chipSize,
  fallback = null,
  className,
}: InvoicedCustomersProps) {
  const gt = useGT();
  if (customers.length === 0) return <>{fallback}</>;

  if (variant === 'list') {
    return (
      <ul
        className={cn('min-w-0 space-y-1', className)}
        aria-label={gt('Invoiced customers', {
          context:
            'a11y label, list of customers whose invoices a payment covers',
        })}
      >
        {customers.map((customer) => (
          <li key={customer.id} className="truncate">
            <NameLink customer={customer} emphasized />
          </li>
        ))}
      </ul>
    );
  }

  const visible = customers.slice(0, 1);
  const hiddenCount = customers.length - visible.length;

  const names = visible.map((customer, index) => (
    <Fragment key={customer.id}>
      {index > 0 && ', '}
      <NameLink customer={customer} emphasized={variant === 'stack'} />
    </Fragment>
  ));
  const more = hiddenCount > 0 && (
    <MoreCustomersPopover
      customers={customers}
      hiddenCount={hiddenCount}
      chipSize={chipSize ?? variant}
    />
  );

  if (variant === 'compact') {
    return (
      <div className={cn('flex min-w-0 items-baseline gap-1.5', className)}>
        <span className="min-w-0 truncate">{names}</span>
        {more}
      </div>
    );
  }

  // One run of inline text, so `+N` follows the last name on the same line
  // instead of dropping to its own row when the names wrap.
  return (
    <div className={cn('min-w-0', className)}>
      {names}
      {/* A non-breaking space: if the line wraps, the last name's final word
          goes with `+N`, so it never sits alone on a row. */}
      {more && (
        <>
          {'\u00A0'}
          {more}
        </>
      )}
    </div>
  );
}
